<template>
	<div class="flex">
		<div ref="reference" class="flex-shrink-0 flex-grow">
			<slot />
		</div>
		<div ref="popup" class="floating" :hidden="!isVisible">
			<slot v-if="isVisible" name="popup" />
		</div>
	</div>
</template>

<script>
import {computePosition, autoUpdate, offset, flip, shift, detectOverflow, size} from '@floating-ui/dom'

export const placements = [
	'auto',
	'auto-start',
	'auto-end',
	'top',
	'top-start',
	'top-end',
	'right',
	'right-start',
	'right-end',
	'bottom',
	'bottom-start',
	'bottom-end',
	'left',
	'left-start',
	'left-end',
]

// see https://floating-ui.com/docs/detectOverflow
const detectOverflowMiddleware = {
	name: 'middleware',
	async fn(state) {
		const overflow = await detectOverflow(state, {padding: 8})
		const result = {
			x: state.x,
			y: state.y,
		}
		if (overflow.right > 0) {
			result.x -= overflow.right
		}
		if (overflow.bottom > 0) {
			result.y -= overflow.bottom
		}
		if (overflow.left > 0) {
			result.x += overflow.left
		}
		if (overflow.top > 0) {
			result.y += overflow.top
		}
		return result
	},
}

export default {
	props: {
		placement: {
			type: String,
			default: () => 'bottom',
			validator: placement => placements.includes(placement),
		},
		modifiers: {
			type: Array,
			default: () => [],
		},
		isVisible: Boolean,
		fixed: Boolean,
	},
	data() {
		return {
			cleanup: null,
		}
	},
	computed: {
		computedOptions() {
			return {
				placement: this.placement,
				middleware: this.computedMiddleware,
			}
		},
		computedMiddleware() {
			const middleware = []

			// offset middleware should always be first
			// see https://floating-ui.com/docs/middleware#ordering
			const offsetModifier = this.modifiers.find(modifier => modifier.name === 'offset') || {
				options: {offset: [0, 8]},
			}
			const [crossAxis, mainAxis] = offsetModifier.options.offset
			middleware.push(offset({mainAxis, crossAxis}))

			// flip middleware
			middleware.push(flip())

			// preventOverflow middleware
			const preventOverflowModifier = this.modifiers.find(modifier => modifier.name === 'preventOverflow') || {
				options: {padding: 8},
			}
			const padding = preventOverflowModifier.options.padding
			middleware.push(shift({padding}))

			middleware.push(
				size({
					padding,
					apply({availableWidth, availableHeight, elements}) {
						Object.assign(elements.floating.style, {
							maxWidth: `${availableWidth}px`,
							maxHeight: `${availableHeight}px`,
						})
					},
				})
			)

			// custom detectOverflow middleware
			// see https://floating-ui.com/docs/detectOverflow
			middleware.push(detectOverflowMiddleware)

			return middleware
		},
	},
	watch: {
		// update popup position after element is rendered
		async isVisible(isVisible) {
			if (isVisible) {
				await this.$nextTick()
				this.updatePosition()
				return
			}
			this.cleanup?.()
		},
	},
	beforeUnmount() {
		this.cleanup?.()
	},
	methods: {
		updatePosition() {
			const reference = this.$refs.reference
			const popup = this.$refs.popup
			if (!reference || !popup) return
			const computeAndSetPosition = () => {
				computePosition(reference, popup, this.computedOptions).then(({x, y}) => {
					Object.assign(popup.style, {
						left: `${x}px`,
						top: `${y}px`,
					})
				})
			}
			if (this.fixed) {
				computeAndSetPosition()
				return
			}
			this.cleanup = autoUpdate(reference, popup, computeAndSetPosition)
		},
	},
}
</script>

<style scoped>
.floating {
	width: max-content;
	position: absolute;
	top: 0;
	left: 0;
	z-index: 3;
}
</style>
